import { useCallback, useMemo } from 'react'
import { ALL_FIELD_TYPES } from '@carto/kepler.gl/constants'
import { AggregationTypes } from '@carto/react-core'
import { FormulaWidget, MapDatasetEnriched, KeplerFieldType } from 'features/builder'
import { isCustomAggregationExpSupported } from 'features/builder/model/widgets/utils'

const availableFieldTypesForColumn: KeplerFieldType[] = [ALL_FIELD_TYPES.integer, ALL_FIELD_TYPES.real]

export function isFormulaWidgetValid(widget: FormulaWidget, { source }: { source: MapDatasetEnriched }): boolean {
  if (widget.operation === AggregationTypes.COUNT) {
    return true
  }
  if (
    widget.operation === AggregationTypes.CUSTOM &&
    isCustomAggregationExpSupported({ source }) &&
    widget.operationExp
  ) {
    return true
  }

  const columnType = source.fields?.find((f) => f.id === widget.column)?.type as KeplerFieldType

  return Boolean(columnType) && availableFieldTypesForColumn.includes(columnType)
}

function useWidgetFormulaModel({ source }: { source: MapDatasetEnriched }) {
  const customAggregationExpSupported = useCallback(() => isCustomAggregationExpSupported({ source }), [source])

  const validateWidget = (widget: FormulaWidget) => {
    const columnType = source.fields?.find((f) => f.id === widget.column)?.type as KeplerFieldType

    switch (widget.operation) {
      case AggregationTypes.COUNT: {
        widget.isValid = true
        if (!availableFieldTypesForColumn.includes(columnType)) {
          widget.column = ''
          widget.spatialIndexAggregation = undefined
        }
        break
      }
      case AggregationTypes.CUSTOM: {
        if (!isCustomAggregationExpSupported({ source })) {
          widget.isValid = false
          widget.operation = AggregationTypes.COUNT
        } else if (!widget.operationExp) {
          widget.isValid = false
        } else {
          widget.isValid = true
        }
        break
      }

      default: {
        if (!widget.column) {
          widget.isValid = false
        } else {
          widget.isValid = true
        }
        break
      }
    }
  }

  const isWidgetAvailable = useMemo(() => {
    return true
  }, [])

  const getAvailableOperations = useCallback(() => {
    const hasSupportedFields = source.fields?.some((f) => availableFieldTypesForColumn.includes(f.type))

    const baseSupportedOperations = hasSupportedFields
      ? Object.values(AggregationTypes)
      : [AggregationTypes.COUNT, AggregationTypes.CUSTOM]

    return baseSupportedOperations.filter((aggType) => {
      if (aggType === AggregationTypes.CUSTOM) {
        return isCustomAggregationExpSupported({ source })
      }
      return true
    })
  }, [source])

  return {
    validateWidget,
    isWidgetAvailable,
    availableFieldTypesForColumn,
    getAvailableOperations,
    customAggregationExpSupported
  }
}

export default useWidgetFormulaModel
